import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";

import {
  getInvoice,
  getOrganization,
  refreshInvoiceCheckoutSession,
  getOrgOrder,
  getInvoiceOrderHistory,
} from "../../utility";
import getStripeSessionInvoice from "../../utility/getStripeSessionInvoice";
import generateInvoiceNumber from "../../utility/generateInvoiceNumber";
import updateInvoice from "../../utility/updateInvoice";
import downloadReceipt from "../../utility/createReceiptPDF";
import LoadingSpinner from "../../components/LoadingSpinner";
import { BackIcon, BasketIcon, CardIcon } from "../../components/Icon";
import { formatOrderDateTime } from "../../utility/orderDateTime";

const INVOICING_FEE_PRODUCT_ID = "invoicing_fee";

const toFiniteNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const formatPrice = (value) =>
  new Intl.NumberFormat("fr-BE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const getProductQuantity = (product) => {
  const quantityRaw = toFiniteNumber(product?.quantity);
  return quantityRaw !== null && quantityRaw > 0 ? quantityRaw : 1;
};

const getProductUnitPrice = (product) => {
  if (typeof product?.unitPriceTvac === "number" && Number.isFinite(product.unitPriceTvac)) {
    return product.unitPriceTvac / 100;
  }

  const price = toFiniteNumber(product?.price);
  return price !== null ? price : 0;
};

const getProductIconVariant = (product) => {
  const name = typeof product?.name === "string" ? product.name.toLowerCase() : "";
  const title = typeof product?.title === "string" ? product.title.toLowerCase() : "";

  if (
    product?.id === INVOICING_FEE_PRODUCT_ID ||
    name.includes("invoicing fee") ||
    title.includes("invoicing fee") ||
    name.includes("facturation") ||
    title.includes("facturation")
  ) {
    return "fee";
  }

  return "product";
};

const InvoiceCheckout = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alert = useAlert();
  const { t } = useTranslation("invoice");
  const [invoice, setInvoice] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(true);
  const [invoiceNotFound, setInvoiceNotFound] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [isReceiptDownloading, setIsReceiptDownloading] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [historyNextCursor, setHistoryNextCursor] = useState(null);
  const [isOrderHistoryLoading, setIsOrderHistoryLoading] = useState(false);
  const [orderHistoryError, setOrderHistoryError] = useState(false);
  const [expandedHistoryOrders, setExpandedHistoryOrders] = useState({});

  const enhanceInvoiceProducts = useCallback((invoiceData) => {
    if (!invoiceData) return [];
    const baseProducts = Array.isArray(invoiceData.products) ? invoiceData.products : [];
    return baseProducts.map((product) => ({
      ...product,
      name: product?.name || product?.title,
    }));
  }, []);

  const computeTotalDue = (invoiceData) => {
    if (!invoiceData) return 0;
    if (typeof invoiceData.totalAmount === "number") {
      return invoiceData.totalAmount;
    }
    if (typeof invoiceData.totalAmountCents === "number") {
      return invoiceData.totalAmountCents / 100;
    }
    if (typeof invoiceData.totalDueCents === "number") {
      return invoiceData.totalDueCents / 100;
    }
    if (typeof invoiceData.amountPaid === "number") {
      return invoiceData.amountPaid / 100;
    }
    return 0;
  };

  const repairInvoiceDocument = useCallback(async (invoiceData, organizationData) => {
    if (!invoiceData || invoiceData.paid) {
      return invoiceData;
    }

    if (!invoiceData.id) {
      return invoiceData;
    }

    let repairedInvoice = { ...invoiceData };

    const preliminaryUpdates = {};

    const organizationStripeAccountId =
      repairedInvoice.stripeAccountId ||
      organizationData?.stripeAccountId ||
      organizationData?.stripeId ||
      organizationData?.stripe?.accountId ||
      organizationData?.stripe?.account?.id ||
      null;

    if (!repairedInvoice.stripeAccountId && organizationStripeAccountId) {
      preliminaryUpdates.stripeAccountId = organizationStripeAccountId;
    }

    if (!repairedInvoice.orgName && organizationData?.organization) {
      preliminaryUpdates.orgName = organizationData.organization;
    }

    if (!repairedInvoice.uid && organizationData?.uid) {
      preliminaryUpdates.uid = organizationData.uid;
    }

    const normalizedOrderIds = Array.isArray(repairedInvoice.orderIds)
      ? repairedInvoice.orderIds
          .map((orderId) => (typeof orderId === "string" ? orderId.trim() : ""))
          .filter(Boolean)
      : [];

    if (!repairedInvoice.orderNumbers && normalizedOrderIds.length > 0) {
      const orderNumbers = normalizedOrderIds
        .map((orderId) => orderId.slice(-4))
        .filter(Boolean)
        .join(", ");

      if (orderNumbers) {
        preliminaryUpdates.orderNumbers = orderNumbers;
      }
    }

    const hasCustomerEmail =
      typeof repairedInvoice.customerEmail === "string" &&
      repairedInvoice.customerEmail.trim() !== "";

    const hasStripeCustomerEmail =
      typeof repairedInvoice.stripeCustomerEmail === "string" &&
      repairedInvoice.stripeCustomerEmail.trim() !== "";

    const organizationId = organizationData?.id || invoiceData?.orgId || null;

    if (
      (!hasCustomerEmail || !hasStripeCustomerEmail) &&
      normalizedOrderIds.length > 0 &&
      organizationId
    ) {
      for (const orderId of normalizedOrderIds) {
        try {
          const orderResponse = await getOrgOrder(organizationId, orderId);
          const orderData = orderResponse?.order || orderResponse || {};
          const derivedEmail =
            orderData.customerEmail ||
            orderData.billingEmail ||
            orderData.email ||
            orderData.customer?.email ||
            null;

          if (derivedEmail) {
            if (!hasCustomerEmail) {
              preliminaryUpdates.customerEmail = derivedEmail;
            }
            if (!hasStripeCustomerEmail) {
              preliminaryUpdates.stripeCustomerEmail = derivedEmail;
            }
            break;
          }
        } catch (error) {
          console.error(`Failed to fetch order ${orderId} while repairing invoice:`, error);
        }
      }
    }

    if (Object.keys(preliminaryUpdates).length > 0) {
      preliminaryUpdates.updatedAt = Date.now();
      try {
        await updateInvoice(repairedInvoice.id, preliminaryUpdates);
        repairedInvoice = { ...repairedInvoice, ...preliminaryUpdates };
      } catch (error) {
        console.error("Failed to update invoice metadata while repairing document:", error);
      }
    }

    const sessionExpiresAt =
      typeof repairedInvoice.sessionExpiresAt === "number"
        ? repairedInvoice.sessionExpiresAt
        : null;
    const sessionMissing = !repairedInvoice.checkoutUrl || !repairedInvoice.stripeSessionId;
    const sessionExpired = sessionExpiresAt ? sessionExpiresAt <= Date.now() : false;
    const sessionStatusExpired =
      repairedInvoice.checkoutSessionStatus === "expired" ||
      repairedInvoice.checkoutPaymentStatus === "expired";

    if (sessionMissing || sessionExpired || sessionStatusExpired) {
      try {
        const refreshed = await refreshInvoiceCheckoutSession(repairedInvoice.id);
        if (refreshed?.invoice) {
          repairedInvoice = { ...repairedInvoice, ...refreshed.invoice };
        }
      } catch (error) {
        console.error("Failed to refresh invoice checkout session:", error);
      }
    }

    const hasInvoiceNumber = !!repairedInvoice.invoiceNumber;
    const shouldGenerateNumber = !hasInvoiceNumber && organizationData && organizationData.uid;
    if (shouldGenerateNumber) {
      const updates = {};

      try {
        const timestamp = toFiniteNumber(repairedInvoice.createdAt) || Date.now();
        const invoiceNumberInfo = await generateInvoiceNumber({
          org: organizationData,
          timestamp,
        });
        if (invoiceNumberInfo) {
          updates.invoiceNumber = invoiceNumberInfo.invoiceNumber || null;
          updates.invoiceNumberPrefix = invoiceNumberInfo.invoiceNumberPrefix || null;
          updates.invoiceNumberSequence = invoiceNumberInfo.invoiceNumberSequence || null;
          updates.invoiceYear = invoiceNumberInfo.invoiceYear || null;
          updates.invoiceMonth = invoiceNumberInfo.invoiceMonth || null;
        }
      } catch (error) {
        console.error("Failed to generate invoice number:", error);
      }

      if (Object.keys(updates).length > 0) {
        updates.updatedAt = Date.now();
        try {
          await updateInvoice(repairedInvoice.id, updates);
          repairedInvoice = { ...repairedInvoice, ...updates };
        } catch (error) {
          console.error("Failed to update invoice while repairing document:", error);
        }
      }
    }

    return repairedInvoice;
  }, []);

  const loadOrderHistory = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (!invoiceId) return;

      setIsOrderHistoryLoading(true);
      setOrderHistoryError(false);
      try {
        const history = await getInvoiceOrderHistory(invoiceId, cursor);
        setOrderHistory((currentHistory) =>
          append ? [...currentHistory, ...history.orders] : history.orders,
        );
        setHistoryNextCursor(history.nextCursor);
      } catch (error) {
        console.error("Failed to load invoice order history:", error);
        setOrderHistoryError(true);
      } finally {
        setIsOrderHistoryLoading(false);
      }
    },
    [invoiceId],
  );

  useEffect(() => {
    const fetchInvoice = async () => {
      setIsInvoiceLoading(true);
      setInvoiceNotFound(false);
      setOrderHistory([]);
      setHistoryNextCursor(null);
      setOrderHistoryError(false);
      setExpandedHistoryOrders({});

      try {
        const data = await getInvoice(invoiceId);

        if (!data) {
          setInvoice(null);
          setProducts([]);
          setTotal(0);
          setIsPaymentComplete(false);
          setInvoiceNotFound(true);
          return;
        }

        let invoiceData = { ...data };
        let organizationData = null;

        if (invoiceData.orgId) {
          try {
            const orgRes = await getOrganization(invoiceData.orgId);
            if (orgRes?.org) {
              organizationData = { id: invoiceData.orgId, ...orgRes.org };
            }
          } catch (error) {
            console.error("Failed to fetch organization:", error);
          }
        }
        setOrganization(organizationData);

        if (!invoiceData.paid) {
          invoiceData = await repairInvoiceDocument(invoiceData, organizationData);
        }

        const productsWithFee = enhanceInvoiceProducts(invoiceData);
        setProducts(productsWithFee);
        setTotal(computeTotalDue(invoiceData));
        setInvoice({ ...invoiceData, products: productsWithFee });
        setIsPaymentComplete(!!invoiceData.paid || invoiceData.checkoutPaymentStatus === "paid");
        void loadOrderHistory();

        if (!invoiceData.paid) {
          const sessionId = searchParams.get("session_id");
          const stripeAccountId = searchParams.get("stripe_account");

          if (sessionId && stripeAccountId) {
            try {
              const refreshed = await getStripeSessionInvoice(stripeAccountId, sessionId);
              if (refreshed?.paid) {
                setIsPaymentComplete(true);
                setInvoice((prev) => ({
                  ...prev,
                  paid: true,
                  amountPaid: refreshed.amountPaid ?? prev?.amountPaid,
                }));
              }
            } catch (error) {
              console.error("Failed to refresh invoice payment status:", error);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch invoice:", error);
        setInvoice(null);
        setProducts([]);
        setTotal(0);
        setOrganization(null);
        setIsPaymentComplete(false);
        setInvoiceNotFound(true);
      } finally {
        setIsInvoiceLoading(false);
      }
    };

    if (invoiceId) {
      fetchInvoice();
    } else {
      setInvoice(null);
      setProducts([]);
      setTotal(0);
      setOrganization(null);
      setIsPaymentComplete(false);
      setInvoiceNotFound(true);
      setIsInvoiceLoading(false);
    }
  }, [invoiceId, searchParams, enhanceInvoiceProducts, repairInvoiceDocument, loadOrderHistory]);

  const handleDownloadReceipt = async () => {
    if (!invoice || isReceiptDownloading) return;

    setIsReceiptDownloading(true);
    try {
      await downloadReceipt({
        invoice: {
          ...invoice,
          products,
        },
        organization,
      });
    } catch (error) {
      console.error("Failed to download receipt:", error);
      alert.show(t("receiptDownloadFailed"), { type: "error" });
    } finally {
      setIsReceiptDownloading(false);
    }
  };

  return (
    <div className="main-page">
      <div className="container px-4 d-flex flex-column" style={{ minHeight: "100dvh" }}>
        <div className="page-title">
          <button type="button" className="back-icon" onClick={() => navigate(-1)}>
            <BackIcon />
          </button>
          {t("title")}
        </div>

        {isInvoiceLoading && (
          <div className="mt-auto text-center pb-4">
            <LoadingSpinner height="30%" width="30%" />
            <div className="mt-3 text-white">{t("loadingInvoice")}</div>
          </div>
        )}

        {!isInvoiceLoading && invoice && (
          <>
            <div className="price--area mb-30">
              <h2 className="price">{total.toFixed(2)}€</h2>
              {invoice?.invoiceNumber && (
                <div className="subtext">
                  {t("invoiceNumber", { number: invoice.invoiceNumber })}
                </div>
              )}
            </div>

            <div className="mb-30">
              <div className="text-22 gilroy-bold text-white mb-15">{t("productDetails")}</div>
              <div className="invoice-products-list">
                {products.map((product, idx) => {
                  const quantity = getProductQuantity(product);
                  const unitPrice = getProductUnitPrice(product);
                  const productTotal = unitPrice * quantity;
                  const orderedAt = formatOrderDateTime(product?.orderedAt);
                  const iconVariant = getProductIconVariant(product);
                  const IconComponent = iconVariant === "fee" ? CardIcon : BasketIcon;

                  return (
                    <div
                      key={`${product?.id || product?.name || "product"}-${idx}`}
                      className="invoice-product-card"
                    >
                      <div
                        className={`invoice-product-card__icon ${
                          iconVariant === "fee" ? "invoice-product-card__icon--fee" : ""
                        }`}
                      >
                        <IconComponent />
                      </div>
                      <div className="invoice-product-card__content">
                        <div className="invoice-product-card__header">
                          <div className="invoice-product-card__name">
                            {product?.name || t("fallbackProductName")}
                          </div>
                          <div className="invoice-product-card__total">
                            {formatPrice(productTotal)}€
                          </div>
                        </div>
                        <div className="invoice-product-card__meta">
                          <span>{t("quantityLabel", { count: quantity })}</span>
                          <span>{t("unitPriceLabel", { price: formatPrice(unitPrice) })}</span>
                          {orderedAt && <span>{t("productOrderedAt", { date: orderedAt })}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {(orderHistory.length > 0 || isOrderHistoryLoading || orderHistoryError) && (
              <section className="invoice-order-history mb-30" aria-labelledby="invoice-order-history-title">
                <h2 id="invoice-order-history-title" className="text-22 gilroy-bold text-white mb-15">
                  {t("orderHistoryTitle")}
                </h2>

                {orderHistory.map((historyOrder) => {
                  const isExpanded = Boolean(expandedHistoryOrders[historyOrder.reference]);
                  const orderedAt = formatOrderDateTime(historyOrder.createdAt);

                  return (
                    <article key={`${historyOrder.reference}-${historyOrder.createdAt}`} className="invoice-history-card">
                      <button
                        type="button"
                        className="invoice-history-card__summary"
                        aria-expanded={isExpanded}
                        onClick={() =>
                          setExpandedHistoryOrders((expanded) => ({
                            ...expanded,
                            [historyOrder.reference]: !isExpanded,
                          }))
                        }
                      >
                        <span>
                          <strong>{t("historyOrderReference", { reference: historyOrder.reference })}</strong>
                          {orderedAt && <small>{orderedAt}</small>}
                        </span>
                        <strong>{formatPrice(historyOrder.totalAmount)}€</strong>
                      </button>

                      {isExpanded && (
                        <div className="invoice-history-card__products">
                          {historyOrder.products.map((product, productIndex) => (
                            <div key={`${product.name}-${productIndex}`} className="invoice-history-card__product">
                              <span>
                                {product.name} · {t("quantityLabel", { count: product.quantity })}
                              </span>
                              <strong>{formatPrice(product.total)}€</strong>
                            </div>
                          ))}
                        </div>
                      )}
                    </article>
                  );
                })}

                {isOrderHistoryLoading && (
                  <p className="invoice-order-history__status">{t("orderHistoryLoading")}</p>
                )}
                {orderHistoryError && (
                  <p className="invoice-order-history__status">{t("orderHistoryUnavailable")}</p>
                )}
                {historyNextCursor && !isOrderHistoryLoading && (
                  <button
                    type="button"
                    className="invoice-order-history__more"
                    onClick={() => loadOrderHistory({ cursor: historyNextCursor, append: true })}
                  >
                    {t("orderHistoryLoadMore")}
                  </button>
                )}
              </section>
            )}

            <>
              <div className="mt-auto text-center">
                <button
                  type="button"
                  className="invoice-receipt-download-btn"
                  onClick={handleDownloadReceipt}
                  disabled={isReceiptDownloading || !isPaymentComplete}
                >
                  {isReceiptDownloading
                    ? t("receiptDownloadInProgress")
                    : isPaymentComplete
                      ? t("downloadReceipt")
                      : t("receiptAvailableAfterPayment")}
                </button>
                <div className="mt-12 pb-4">{t("invoiceDeliveryNotice")}</div>
              </div>
            </>
          </>
        )}

        {!isInvoiceLoading && (!invoice || invoiceNotFound) && (
          <div className="mt-auto text-center pb-4">
            <div className="text-white">{t("invoiceNotFound")}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceCheckout;
